import { useQueueToast } from '@/hooks/useToastPopup.tsx';
import useAlert from '@/hooks/useAlert';
import usePreservedCallback from '@/hooks/react/usePreservedCallback';
import { isApiError } from '@/api/error/api.errors';
import { ApiResponseCode } from '@/constants';
import useUpdateTermsAgreement from '@/features/apply-event/hooks/useUpdateTermsAgreement';
import { TermsCategory } from '@/api/terms.types';
import useNeedsToAgreeEventTerms from './useIsAgreedRequiredTerms';
import useEventEnterMutation from '@/queries/hooks/useEventEnterMutation';

export default function useEventSubmit() {
  const popup = useAlert();
  const toast = useQueueToast();

  const updateTermsAgreemnt = useUpdateTermsAgreement();
  const { mutateAsync: submit } = useEventEnterMutation();

  const needsToAgreeEventTerms = useNeedsToAgreeEventTerms();

  const handler = usePreservedCallback(async (id: string) => {
    if (needsToAgreeEventTerms) {
      await updateTermsAgreemnt({
        [TermsCategory.INFO_COLLECTION]: true,
        [TermsCategory.RECEIVE_MARKETING_INFO]: true,
      });

      const date = new Date().toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      toast({
        message: `서비스 품질 향상을 위한 개인정보 수집·이용 동의가 되었어요.\n동의 일자 : ${date}`,
        ms: 2500,
      });
    }

    await submit(id);
    toast({ message: '이벤트 참여가 완료되었어요' });
  });

  const errorHandler = usePreservedCallback((error: unknown) => {
    if (isApiError(error)) {
      switch (error.code) {
        case ApiResponseCode.UNDER_14:
          popup({
            title: '아쉽게도, 응모를 할 수 없어요',
            description: '만14세 이상 고객만 참여하실 수 있어요.',
          });
          return;
        case ApiResponseCode.NOT_NEW_USER:
          popup({
            title: '아쉽게도, 응모를 할 수 없어요',
            description:
              '이벤트 기간 내 첫 로그인 고객님만 참여하실 수 있어요.',
          });
          return;
        default:
          popup({
            title: '아쉽게도, 응모를 할 수 없어요',
            description: `예기치 못한 문제가 발생했어요.`,
          });
          return;
      }
    }

    popup({
      title: '아쉽게도, 응모를 할 수 없어요',
      description: `예기치 못한 문제가 발생했어요.`,
    });
  });

  return usePreservedCallback(async (id: string) => {
    try {
      await handler(id);
    } catch (error) {
      errorHandler(error);
    }
  });
}
